import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import io, { Socket } from 'socket.io-client';
import { useAuth } from '../../contexts/AuthContext';
import { useMode } from '../../contexts/ModeContext';
import ChatRoomList from './ChatRoomList';
import QuoteModal, { QuoteData } from './QuoteModal';
import QuoteMessage from './QuoteMessage';
import ProposalMessage from './ProposalMessage';
import ContractMessage from './ContractMessage';
import ContractModal, { ContractData } from './ContractModal';
import TradeModal, { TradeFormData } from './TradeModal';
import TradeMessage from './TradeMessage';
import { ChatRoom, ChatMessage } from '../../types';
import { API_ORIGIN, apiBaseUrl } from '../../config/runtime';
import { localizeSystemMessage } from '../../i18n/systemMessages';
import './ChatPage.css';
import './QuoteModal.css';
import './ProposalMessage.css';
import './ContractMessage.css';
import './ContractModal.css';
import './TradeModal.css';
import './TradeMessage.css';

const ChatPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { currentMode, isClientMode, isFreelancerMode } = useMode();
  const { t, i18n } = useTranslation();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [activeRoom, setActiveRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<{[key: string]: boolean}>({});
  const [loading, setLoading] = useState(true);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [contractInitialData, setContractInitialData] = useState<any>(null);
  const [currentTrade, setCurrentTrade] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const socketRef = useRef<Socket | null>(null);

  // Socket.io 연결
  const loadRoomMessages = useCallback(async (roomId: number) => {
    try {
      const response = await fetch(apiBaseUrl(`/chat/room/${roomId}/messages`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages);
        scrollToBottom();
        if (socketRef.current) socketRef.current.emit('mark_as_read', { roomId });
      }
    } catch (err) {
      console.error('메시지 로드 실패:', err);
    }
  }, [token]); // socket 의존성 제거

  useEffect(() => {
    if (!token || !user) {
      navigate('/login');
      return;
    }

    console.log('Socket.io 연결 시도... (사용자:', user.nickname, ')');
    const newSocket = io(API_ORIGIN, {
      auth: { token: token }
    });

    newSocket.on('connect', () => {
      console.log('Socket.io 연결 성공!', newSocket.id);
      newSocket.emit('join_rooms');
    });

    newSocket.on('rooms_list', (roomsList: ChatRoom[]) => {
      console.log('채팅방 목록 수신:', roomsList);
      setRooms(roomsList);
      setLoading(false);
      
      if (roomId) {
        const targetRoom = roomsList.find(room => room.id === parseInt(roomId));
        if (targetRoom) {
          setActiveRoom(targetRoom);
          loadRoomMessages(targetRoom.id);
        }
      }
    });

    newSocket.on('new_message', (message: ChatMessage) => {
      console.log('새 메시지 수신:', message);
      setMessages(prev => [...prev, message]);
      
      setRooms(prev => prev.map(room => {
        if (room.id === message.room_id) {
          return {
            ...room,
            last_message: message.message,
            last_message_time: message.created_at,
            unread_count: message.sender_id !== user.id ? room.unread_count + 1 : room.unread_count
          };
        }
        return room;
      }));
      
      scrollToBottom();
    });

    newSocket.on('user_typing', ({ roomId: typingRoomId, userId, isTyping: typing }: {
      roomId: number; userId: number; isTyping: boolean;
    }) => {
      setTypingUsers(prev => ({
        ...prev,
        [`${typingRoomId}_${userId}`]: typing
      }));
    });

    newSocket.on('room_created', ({ room, messages: roomMessages }: {
      room: ChatRoom; messages: ChatMessage[];
    }) => {
      console.log('채팅방 생성/로드:', room);
      setActiveRoom(room);
      setMessages(roomMessages);
      scrollToBottom();
    });

    newSocket.on('more_messages', ({ roomId: moreRoomId, messages: moreMessages }: {
      roomId: number; messages: ChatMessage[];
    }) => {
      setMessages(prev => {
        if (prev.length > 0 && prev[0].room_id === moreRoomId) {
          return [...moreMessages, ...prev];
        }
        return prev;
      });
    });

    newSocket.on('messages_read', ({ roomId: readRoomId, readBy }: {
      roomId: number; readBy: number;
    }) => {
      setMessages(prev => prev.map(msg =>
        msg.room_id === readRoomId ? { ...msg, is_read: true } : msg
      ));
      if (readBy === user.id) {
        setRooms(prev => prev.map(room => 
          room.id === readRoomId ? { ...room, unread_count: 0 } : room
        ));
      }
    });

    newSocket.on('error', (error: { message: string }) => {
      console.error('Socket 에러:', error);
      alert(error.message);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket.io 연결 해제');
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      newSocket.close();
      socketRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id]); // roomId, loadRoomMessages 제거 → 무한루프 방지

  // roomId URL 파라미터 변경 시 해당 방 메시지 로드
  useEffect(() => {
    if (!roomId || rooms.length === 0) return;
    const targetRoom = rooms.find(room => room.id === parseInt(roomId));
    if (targetRoom) {
      setActiveRoom(targetRoom);
      loadRoomMessages(targetRoom.id);
    }
  }, [roomId, rooms, loadRoomMessages]);

  // 상단 의뢰자/전문가 토글에 맞는 채팅방만 목록에 노출.
  // (상대방 표시는 방의 실제 역할로 고정하는 것과 별개로, 목록 자체는
  // "이 모드로 참여 중인 방"만 보여줘야 한다는 요청에 따라 필터링한다.)
  const visibleRooms = useMemo(() => {
    if (!user) return rooms;
    return rooms.filter(room =>
      isClientMode ? room.client_id === user.id : room.freelancer_id === user.id
    );
  }, [rooms, isClientMode, user]);

  // 의뢰자/전문가 모드를 전환하면, 보고 있던 채팅방과 무관하게
  // 해당 모드의 채팅방 목록으로 새로고침한다(첫 마운트 시에는 실행하지 않음).
  const prevModeRef = useRef(currentMode);
  useEffect(() => {
    if (prevModeRef.current === currentMode) return;
    prevModeRef.current = currentMode;
    setActiveRoom(null);
    setMessages([]);
    navigate('/chat', { replace: true });
  }, [currentMode, navigate]);

  const handleRoomUpdate = (roomId: number, updates: Partial<ChatRoom>) => {
    setRooms(prev => prev.map(room => 
      room.id === roomId ? { ...room, ...updates } : room
    ));
    if (activeRoom?.id === roomId) {
      setActiveRoom(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  const handleRoomSelect = (room: ChatRoom) => {
    setActiveRoom(room);
    setMessages([]);
    navigate(`/chat/${room.id}`, { replace: true });
    loadRoomMessages(room.id);
  };

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeRoom || !socket) return;
    
    socket.emit('send_message', {
      roomId: activeRoom.id,
      message: newMessage.trim(),
      messageType: 'text'
    });
    
    setNewMessage('');
    stopTyping();
  };

  const startTyping = () => {
    if (!isTyping && activeRoom && socket) {
      setIsTyping(true);
      socket.emit('typing_start', { roomId: activeRoom.id });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => stopTyping(), 2000);
  };

  const stopTyping = () => {
    if (isTyping && activeRoom && socket) {
      setIsTyping(false);
      socket.emit('typing_stop', { roomId: activeRoom.id });
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const loadMoreMessages = () => {
    if (!activeRoom || !socket || messages.length === 0) return;
    socket.emit('load_more_messages', {
      roomId: activeRoom.id,
      beforeMessageId: messages[0].id
    });
  };

  // 견적 보내기 (서비스용)
  const handleSendQuote = async (quoteData: QuoteData) => {
    if (!activeRoom || !token) return;
    
    setQuoteLoading(true);
    try {
      const response = await fetch(apiBaseUrl(`/chat/room/${activeRoom.id}/quote`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(quoteData)
      });
      
      const result = await response.json();
      if (result.success) {
        setMessages(prev => [...prev, result.data]);
        setShowQuoteModal(false);
        scrollToBottom();
      } else {
        alert(result.message || t('chat.quoteSendFailed'));
      }
    } catch (err) {
      console.error('견적 전송 오류:', err);
      alert(t('chat.quoteSendError'));
    } finally {
      setQuoteLoading(false);
    }
  };

  // 최종 제안서 보내기 (계약서)
  const handleSendContract = async (contractData: ContractData) => {
    if (!activeRoom || !token) return;
    
    setContractLoading(true);
    try {
      const response = await fetch(apiBaseUrl(`/contract/room/${activeRoom.id}/contract`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          projectId: contractData.projectId,
          serviceId: contractData.serviceId,
          title: contractData.title,
          description: contractData.description,
          finalAmount: contractData.price,
          startDate: contractData.startDate,
          endDate: contractData.endDate,
          deliverables: contractData.deliverables
        })
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom.id);
        setShowContractModal(false);
        setContractInitialData(null);
      } else {
        alert(result.message || t('chat.contractSendFailed'));
      }
    } catch (err) {
      console.error('제안서 전송 오류:', err);
      alert(t('chat.contractSendError'));
    } finally {
      setContractLoading(false);
    }
  };

  // 견적 수락 (서비스용)
  const handleAcceptQuote = async (messageId: number) => {
    if (!token) return;
    if (!window.confirm(t('chat.confirmAcceptQuote'))) return;
    
    try {
      const response = await fetch(apiBaseUrl(`/chat/quote/${messageId}/accept`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        setMessages(prev => prev.map(msg => 
          msg.id === messageId ? { ...msg, quote_status: 'accepted' } : msg
        ));
        alert(t('chat.quoteAccepted'));
        navigate(`/payment?orderId=${result.data.orderId}&amount=${result.data.price}&orderName=${encodeURIComponent(t('chat.quoteOrderName'))}`);
      } else {
        alert(result.message || t('chat.quoteAcceptFailed'));
      }
    } catch (err) {
      console.error('견적 수락 오류:', err);
      alert(t('chat.quoteAcceptError'));
    }
  };

  // 견적 거절
  const handleRejectQuote = async (messageId: number) => {
    const reason = prompt(t('chat.rejectReasonPrompt'));
    
    try {
      const response = await fetch(apiBaseUrl(`/chat/quote/${messageId}/reject`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reason })
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom!.id);
      } else {
        alert(result.message || t('chat.quoteRejectFailed'));
      }
    } catch (err) {
      console.error('견적 거절 오류:', err);
      alert(t('chat.quoteRejectError'));
    }
  };

  // 프로젝트 제안서 수락 (의뢰자)
  const handleAcceptProposal = async (messageId: number) => {
    if (!token) return;
    if (!window.confirm(t('chat.confirmAcceptProposal'))) return;
    
    try {
      const response = await fetch(apiBaseUrl(`/contract/proposal/${messageId}/accept`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom!.id);
        alert(t('chat.proposalAccepted'));
      } else {
        alert(result.message || t('chat.proposalAcceptFailed'));
      }
    } catch (err) {
      console.error('제안서 수락 오류:', err);
      alert(t('chat.proposalAcceptError'));
    }
  };

  // 프로젝트 제안서 거절 (의뢰자)
  const handleRejectProposal = async (messageId: number) => {
    const reason = prompt(t('chat.rejectReasonPrompt'));
    
    try {
      const response = await fetch(apiBaseUrl(`/contract/proposal/${messageId}/reject`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reason })
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom!.id);
      } else {
        alert(result.message || t('chat.proposalRejectFailed'));
      }
    } catch (err) {
      console.error('제안서 거절 오류:', err);
      alert(t('chat.proposalRejectError'));
    }
  };

  // 최종 제안서 작성 시작 (전문가)
  const handleStartContract = (messageId: number, proposalData: any) => {
    setContractInitialData({
      projectTitle: proposalData.projectTitle,
      proposedBudget: proposalData.proposedBudget,
      proposedDuration: proposalData.proposedDuration,
      projectId: proposalData.projectId
    });
    setShowContractModal(true);
  };

  // 계약서 동의
  const handleAgreeContract = async (messageId: number) => {
    if (!token) return;
    if (!window.confirm(t('chat.confirmAgreeContract'))) return;
    
    try {
      const response = await fetch(apiBaseUrl(`/contract/contract/${messageId}/agree`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom!.id);
        if (result.data.bothAgreed) {
          alert(t('chat.bothAgreed'));
        } else {
          alert(t('chat.agreedWaiting'));
        }
      } else {
        alert(result.message || t('chat.contractAgreeFailed'));
      }
    } catch (err) {
      console.error('계약 동의 오류:', err);
      alert(t('chat.contractAgreeError'));
    }
  };

  // 계약서 거절
  const handleRejectContract = async (messageId: number) => {
    alert(t('chat.contractEditNotice'));
  };

  // ========== 직접거래 관련 함수 ==========

  // 현재 채팅방의 거래 상태 조회
  const loadCurrentTrade = useCallback(async (roomId: number) => {
    try {
      const response = await fetch(apiBaseUrl(`/trade/room/${roomId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) setCurrentTrade(result.trade);
    } catch (err) {
      console.error('거래 조회 오류:', err);
    }
  }, [token]);

  // 거래 시작
  const handleStartTrade = async (tradeData: TradeFormData) => {
    if (!activeRoom || !token) return;
    
    setTradeLoading(true);
    try {
      const response = await fetch(apiBaseUrl('/trade/start'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          roomId: activeRoom.id,
          title: tradeData.title,
          description: tradeData.description,
          agreedPrice: tradeData.agreedPrice
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setShowTradeModal(false);
        loadRoomMessages(activeRoom.id);
        setCurrentTrade(result.trade);
      } else {
        alert(result.message || t('chat.tradeStartFailed'));
      }
    } catch (err) {
      console.error('거래 시작 오류:', err);
      alert(t('chat.tradeStartError'));
    } finally {
      setTradeLoading(false);
    }
  };

  // 거래 수락
  const handleAcceptTrade = async (tradeId: number) => {
    if (!token || !activeRoom) return;
    if (!window.confirm(t('chat.confirmAcceptTrade'))) return;
    
    try {
      const response = await fetch(apiBaseUrl(`/trade/${tradeId}/accept`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom.id);
        loadCurrentTrade(activeRoom.id);
        alert(t('chat.tradeAccepted'));
      } else {
        alert(result.message || t('chat.tradeAcceptFailed'));
      }
    } catch (err) {
      console.error('거래 수락 오류:', err);
      alert(t('chat.tradeAcceptError'));
    }
  };

  // 거래 완료 확인
  const handleCompleteTrade = async (tradeId: number) => {
    if (!token || !activeRoom) return;
    if (!window.confirm(t('chat.confirmCompleteTrade'))) return;
    
    try {
      const response = await fetch(apiBaseUrl(`/trade/${tradeId}/complete`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom.id);
        loadCurrentTrade(activeRoom.id);
        if (result.trade.status === 'completed') {
          alert(t('chat.tradeCompleted'));
        } else {
          alert(t('chat.tradeCompleteWaiting'));
        }
      } else {
        alert(result.message || t('chat.tradeCompleteFailed'));
      }
    } catch (err) {
      console.error('거래 완료 오류:', err);
      alert(t('chat.tradeCompleteError'));
    }
  };

  // 거래 취소
  const handleCancelTrade = async (tradeId: number) => {
    if (!token || !activeRoom) return;
    const reason = prompt(t('chat.cancelReasonPrompt'));
    if (reason === null) return; // 취소 버튼 클릭
    
    try {
      const response = await fetch(apiBaseUrl(`/trade/${tradeId}/cancel`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reason })
      });
      
      const result = await response.json();
      if (result.success) {
        loadRoomMessages(activeRoom.id);
        setCurrentTrade(null);
        alert(t('chat.tradeCancelled'));
      } else {
        alert(result.message || t('chat.tradeCancelFailed'));
      }
    } catch (err) {
      console.error('거래 취소 오류:', err);
      alert(t('chat.tradeCancelError'));
    }
  };

  // 채팅방 선택 시 거래 상태도 로드
  useEffect(() => {
    if (activeRoom && token) {
      loadCurrentTrade(activeRoom.id);
    } else {
      setCurrentTrade(null);
    }
  }, [activeRoom, token, loadCurrentTrade]);

  // 상대방 정보는 전역 모드(isClientMode)가 아니라, 이 채팅방에서 내가 실제로
  // client인지 freelancer인지로 결정해야 한다. 그렇지 않으면 상단 모드 전환
  // 버튼을 누를 때마다 같은 채팅방인데도 상대방 아이디가 바뀌어 보이는 버그가 생긴다.
  const getOtherUser = (room: ChatRoom) => {
    const amClientInThisRoom = room.client_id === user?.id;
    return {
      nickname: amClientInThisRoom
        ? room.freelancer_nickname || room.freelancer_name || t('nav.expert')
        : room.client_nickname || room.client_name || t('nav.client'),
      profileImage: amClientInThisRoom ? room.freelancer_profile_image : room.client_profile_image
    };
  };

  const getRoomTitle = (room: ChatRoom) => room.room_name || getOtherUser(room).nickname;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return t('chat.today');
    if (date.toDateString() === yesterday.toDateString()) return t('chat.yesterday');
    return date.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // 현재 채팅방에서의 역할 확인 (모드와 상관없이 채팅방에서의 실제 역할)
  const isClientInRoom = activeRoom ? activeRoom.client_id === user?.id : false;

  // 메시지 렌더링
  const renderMessage = (message: ChatMessage, index: number) => {
    const isMyMessage = message.sender_id === user?.id;
    const showDate = index === 0 || 
      formatDate(messages[index - 1].created_at) !== formatDate(message.created_at);

    // AI 계약서 초안 생성 알림 — 노란색으로 강조 + 주의문구 + 확인 버튼 (양쪽 다 클릭 가능)
    if (message.message_type === 'ai_contract_draft') {
      return (
        <React.Fragment key={message.id}>
          {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
          <div style={{
            margin: '10px auto', maxWidth: 420, padding: '14px 16px',
            background: '#fffbeb', border: '1px solid #fcd34d',
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#78350f', marginBottom: 6 }}>
              {localizeSystemMessage(message.message)}
            </div>
            <div style={{ fontSize: 12, color: '#b45309', lineHeight: 1.6, marginBottom: 10 }}>
              {t('chat.aiContractCaution')}
            </div>
            <button
              onClick={() => navigate(`/contract-ai/${message.room_id}`)}
              style={{
                padding: '7px 14px', borderRadius: 8, border: '1px solid #d97706',
                background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {t('chat.aiContractViewBtn')}
            </button>
          </div>
        </React.Fragment>
      );
    }

    // 시스템 메시지 (거래 관련 포함)
    if (['quote_accepted', 'quote_rejected', 'proposal_accepted', 'proposal_rejected', 'contract_agreed', 'system', 'trade_start', 'trade_accept', 'trade_complete', 'trade_confirmed'].includes(message.message_type)) {
      // 거래 메시지인 경우 TradeMessage 컴포넌트 사용
      if (['trade_start', 'trade_accept', 'trade_complete', 'trade_confirmed'].includes(message.message_type) && currentTrade) {
        return (
          <React.Fragment key={message.id}>
            {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
            <TradeMessage
              tradeData={currentTrade}
              messageType={message.message_type}
              isMyMessage={isMyMessage}
              isClient={isClientInRoom}
              onAccept={handleAcceptTrade}
              onComplete={handleCompleteTrade}
              onCancel={handleCancelTrade}
            />
          </React.Fragment>
        );
      }
      // 일반 시스템 메시지
      return (
        <React.Fragment key={message.id}>
          {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
          <div className={`system-message ${message.message_type}`}>
            <div className="message-text">{localizeSystemMessage(message.message)}</div>
          </div>
        </React.Fragment>
      );
    }

    // 견적 메시지 (서비스용) - 채팅방 역할 기준
    if (message.message_type === 'quote' && message.quote_data) {
      const quoteData = typeof message.quote_data === 'string' 
        ? JSON.parse(message.quote_data) 
        : message.quote_data;

      return (
        <React.Fragment key={message.id}>
          {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
          <QuoteMessage
            messageId={message.id}
            quoteData={quoteData}
            quoteStatus={message.quote_status || 'pending'}
            isMyMessage={isMyMessage}
            isClient={isClientInRoom}
            onAccept={handleAcceptQuote}
            onReject={handleRejectQuote}
          />
        </React.Fragment>
      );
    }

    // 프로젝트 제안서 메시지 - 채팅방 역할 기준
    if (message.message_type === 'project_proposal' && message.proposal_data) {
      const proposalData = typeof message.proposal_data === 'string'
        ? JSON.parse(message.proposal_data)
        : message.proposal_data;

      return (
        <React.Fragment key={message.id}>
          {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
          <ProposalMessage
            messageId={message.id}
            proposalData={proposalData}
            proposalStatus={message.proposal_status || 'pending'}
            isMyMessage={isMyMessage}
            isClient={isClientInRoom}
            onAccept={handleAcceptProposal}
            onReject={handleRejectProposal}
            onStartContract={handleStartContract}
          />
        </React.Fragment>
      );
    }

    // 최종 계약서 메시지 - 채팅방 역할 기준
    if (message.message_type === 'contract' && message.proposal_data) {
      const contractData = typeof message.proposal_data === 'string'
        ? JSON.parse(message.proposal_data)
        : message.proposal_data;

      return (
        <React.Fragment key={message.id}>
          {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
          <ContractMessage
            messageId={message.id}
            contractData={contractData}
            isMyMessage={isMyMessage}
            isClient={isClientInRoom}
            clientAgreed={contractData.clientAgreed || false}
            freelancerAgreed={contractData.freelancerAgreed || false}
            onAgree={handleAgreeContract}
            onReject={handleRejectContract}
          />
        </React.Fragment>
      );
    }

    // 일반 메시지
    return (
      <React.Fragment key={message.id}>
        {showDate && <div className="message-date-divider">{formatDate(message.created_at)}</div>}
        <div className={`message ${isMyMessage ? 'my-message' : 'other-message'}`}>
          {!isMyMessage && <div className="message-sender">{message.sender_nickname}</div>}
          <div className="message-bubble">
            <div className="message-text">{message.message}</div>
            <div className="message-time">
              {formatTime(message.created_at)}
              {isMyMessage && message.is_read && <span className="read-indicator"> ✓✓</span>}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  if (!user) {
    return (
      <div className="chat-loading">
        <div className="spinner"></div>
        <p>{t('chat.loadingUser')}</p>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-sidebar">
        <ChatRoomList
          rooms={visibleRooms}
          activeRoom={activeRoom}
          currentUser={user}
          onRoomSelect={handleRoomSelect}
          onRoomUpdate={handleRoomUpdate}
          loading={loading}
        />
      </div>

      <div className="chat-main">
        {activeRoom ? (
          <>
            <div className="chat-header">
              <div className="chat-header-info">
                <h3>{getRoomTitle(activeRoom)}</h3>
                {activeRoom.project_title && (
                  <span className="project-title">📋 {activeRoom.project_title}</span>
                )}
              </div>
              <div className="chat-header-actions">
                {/* 거래 버튼 - 현재 거래 상태에 따라 표시 */}
                {currentTrade && currentTrade.status === 'in_progress' ? (
                  <button 
                    className="trade-btn in-progress"
                    onClick={() => handleCompleteTrade(currentTrade.id)}
                    title={t('chat.completeTradeTitle')}
                  >
                    🎉 {t('chat.tradeCompleteBtn')}
                  </button>
                ) : currentTrade && currentTrade.status === 'negotiating' ? (
                  <span className="trade-status-badge negotiating">📋 {t('chat.negotiating')}</span>
                ) : (
                  <button 
                    className="trade-btn"
                    onClick={() => setShowTradeModal(true)}
                    title={t('chat.startTradeTitle')}
                  >
                    🤝 {t('chat.tradeBtn')}
                  </button>
                )}
                {isFreelancerMode && (
                  <>
                    <button 
                      className="quote-btn" 
                      onClick={() => setShowQuoteModal(true)}
                      title={t('chat.quoteBtnTitle')}
                    >
                      💰 {t('chat.quoteBtn')}
                    </button>
                  </>
                )}
                <button
                  className="trade-btn"
                  onClick={() => navigate(`/contract-ai/${activeRoom.id}`)}
                  title="채팅 내용으로 AI 계약서 초안 만들기 (건당 490원/12건 4,990원)"
                >
                  📄 AI 계약서 초안
                </button>
                <button className="chat-settings-btn" title={t('chat.settings')}>⚙️</button>
              </div>
            </div>

            <div className="chat-messages" onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (target.scrollTop === 0) loadMoreMessages();
            }}>
              {messages.map((message, index) => renderMessage(message, index))}
              
              {Object.entries(typingUsers).some(([key, value]) => 
                key.startsWith(`${activeRoom.id}_`) && value && !key.endsWith(`_${user.id}`)
              ) && (
                <div className="typing-indicator-container">
                  <div className="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                  <span className="typing-text">{t('chat.typing')}</span>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <form className="chat-input-form" onSubmit={sendMessage}>
              <div className="chat-input-container">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => { setNewMessage(e.target.value); startTyping(); }}
                  placeholder={t('chat.msgPlaceholder')}
                  className="chat-input"
                />
                <button type="submit" className="send-button" disabled={!newMessage.trim()} title={t('chat.sendMsg')}>
                  ➤
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="no-chat-selected">
            <div className="no-chat-icon">💬</div>
            <h3>{t('chat.startConvo')}</h3>
            <p>
              {isClientMode 
                ? t('chat.clientHint')
                : t('chat.freelancerHint')
              }
            </p>
          </div>
        )}
      </div>

      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        onSubmit={handleSendQuote}
        loading={quoteLoading}
      />

      <ContractModal
        isOpen={showContractModal}
        onClose={() => {
          setShowContractModal(false);
          setContractInitialData(null);
        }}
        onSubmit={handleSendContract}
        loading={contractLoading}
        initialData={contractInitialData}
        type="project"
      />

      <TradeModal
        isOpen={showTradeModal}
        onClose={() => setShowTradeModal(false)}
        onSubmit={handleStartTrade}
        loading={tradeLoading}
      />
    </div>
  );
};

export default ChatPage;
